import {
  UserProfile,
  MonteCarloResponse,
  IncomeStage,
  SpendingStage,
  OtherIncomeSource
} from '../types/advanced-retirement';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/advanced-retirement';

// Backend LifeExpectancyScenario enum: 1 = Conservative, 2 = Moderate ("General"), 3 = Aggressive ("Optimistic")
const toLifeExpectancyCode = (scenario?: string | null): number | null => {
  if (!scenario) return null;
  if (scenario.startsWith('Conservative')) return 1;
  if (scenario.startsWith('Optimistic')) return 3;
  return 2;
};

// UI stages are camelCase; the backend IncomeStage model is snake_case
const toBackendIncomeStages = (stages?: IncomeStage[] | null) =>
  stages ? stages.map(stage => ({
    stage: stage.stage,
    starting_age: stage.startingAge,
    ending_age: stage.endingAge,
    starting_income: stage.startingIncome,
    ending_income: stage.endingIncome,
    rrsp_contributions: stage.rrspContributions || 0
  })) : null;

// Other Income cards are camelCase; the backend OtherIncomeSource model is snake_case. Cards with no amount are noise.
// Older saved cards used profiles 'Steady' / 'increasing' / 'decreasing'; the engine models one-time or ongoing only.
export const toBackendOtherIncomeSources = (sources?: OtherIncomeSource[] | null) => {
  const fundedCards = (sources || []).filter(source => source.amount > 0 && Number.isFinite(source.startAge));
  if (fundedCards.length === 0) return null;
  return fundedCards.map(source => ({
    amount: source.amount,
    start_age: source.startAge,
    end_age: source.endAge ?? null,
    profile: source.profile === 'one time' ? 'one time' : 'ongoing',
    income_type: source.incomeType || 'regular',
    cost_base: source.incomeType === 'capital-gains' ? (source.costBase || 0) : 0,
    description: source.description || null
  }));
};

// Capital Gains "Year" inputs default to ages (65, 70, ...). Backend needs a calendar year >= 2024, and only when there is a gain
const toCapitalGainsYear = (profile: UserProfile, amount?: number, yearOrAge?: number): number | null => {
  if (!amount || !yearOrAge) return null;
  const isAge = yearOrAge < 1900;
  return isAge ? profile.currentYear + (yearOrAge - profile.age) : yearOrAge;
};

// The engine reads one flat annual_spending and nothing in the UI writes it (hidden 60,000 default).
// Until the backend consumes the stage tables, send the stages that cover retirement age (personal stages add to shared).
const spendingAtRetirement = (profile: UserProfile): number => {
  const personalStages = profile.personalSpendingEnabled ? profile.personalSpendingStages || [] : [];
  const stages = [...profile.sharedSpendingStages, ...personalStages];
  const coversRetirement = (stage: SpendingStage) => stage.startingAge <= profile.retirementAge && profile.retirementAge <= stage.endingAge;
  const spendingCovered = stages.filter(coversRetirement).reduce((sum, stage) => sum + (stage.amount || 0), 0);
  if (spendingCovered > 0) return spendingCovered;
  return profile.sharedSpendingStages[0]?.amount || profile.annualSpending;
};

// "Start at 65" / "Start early at 60" -> 65 / 60. No number ("Calculate Suggested Strategy") -> 65. OAS cannot start before 65.
const toBenefitStartAge = (strategy: string | undefined, minimumAge: number): number => {
  const match = (strategy || '').match(/\d+/);
  const age = match ? parseInt(match[0], 10) : 65;
  return Math.max(minimumAge, age);
};

// Surface the backend's error text (e.g. "YOUR RETIREMENT PLAN IS NOT VIABLE ...") instead of a bare status code
const throwServerError = async (response: Response): Promise<never> => {
  let message = `HTTP error! status: ${response.status}`;
  try {
    const body = await response.json();
    if (body && body.error) message = body.error;
  } catch {
    // body was not JSON
  }
  throw new Error(message);
};

export class AdvancedRetirementAPI {
  // Convert backend test scenario data to frontend UserProfile format
  static convertBackendToFrontendFormat(backendData: any): UserProfile {
    return {
      // Personal Information
      age: backendData.age || 45,
      gender: backendData.gender || 'Male',
      lifeExpectancyScenario: backendData.lifeExpectancyScenario || 'General',
      spouseAge: backendData.spouseAge,
      spouseGender: backendData.spouseGender,
      spouseLifeExpectancyScenario: backendData.spouseLifeExpectancyScenario,
      
      // General Settings
      currentYear: backendData.currentYear || 2025,
      retirementAge: backendData.retirementAge || 65,
      lifeExpectancy: backendData.lifeExpectancy || 85,
      numScenarios: backendData.numScenarios || 100,
      province: backendData.province || 'Ontario',
      maritalStatus: backendData.maritalStatus || 'Single',
      
      // Income Information - NEW TABLE STRUCTURE
      incomeStages: backendData.incomeStages || [
        {
          stage: 1,
          startingAge: backendData.age || 45,
          endingAge: (backendData.retirementAge || 65) - 1,
          startingIncome: backendData.currentIncome || 80000,
          endingIncome: backendData.currentIncome || 80000,
          rrspContributions: 0
        }
      ],
      spouseIncomeStages: backendData.spouseIncomeStages,
      
      // Legacy income fields (keep for backward compatibility)
      currentIncome: backendData.currentIncome || 80000,
      expectedRetirementIncome: backendData.expectedRetirementIncome || 60000,
      spouseCurrentIncome: backendData.spouseCurrentIncome,
      spouseExpectedRetirementIncome: backendData.spouseExpectedRetirementIncome,
      
      // Financial Balances
      rrspBalance: backendData.rrspBalance || 250000,
      rrifBalance: backendData.rrifBalance || 0,
      liraBalance: backendData.liraBalance || 0,
      tfsaBalance: backendData.tfsaBalance || 50000,
      tfsaContributionRoom: backendData.tfsaContributionRoom || 88000,
      rrspContributionRoom: backendData.rrspContributionRoom || 0,      tfsaAnnualIncrease: backendData.tfsaAnnualIncrease || 7000,
      nonRegisteredBalance: backendData.nonRegisteredBalance || 100000,
      corporateBalance: backendData.corporateBalance || 0,
      
      // Monthly Contributions (with checkboxes)
      rrspMonthlyContribution: backendData.rrspMonthlyContribution || 0,
      rrspMonthlyContributionEnabled: backendData.rrspMonthlyContributionEnabled || false,
      tfsaMonthlyContribution: backendData.tfsaMonthlyContribution || 0,
      tfsaMonthlyContributionEnabled: backendData.tfsaMonthlyContributionEnabled || false,
      nonRegisteredMonthlyContribution: backendData.nonRegisteredMonthlyContribution || 0,
      nonRegisteredMonthlyContributionEnabled: backendData.nonRegisteredMonthlyContributionEnabled || false,
      corporateMonthlyContribution: backendData.corporateMonthlyContribution || 0,
      corporateMonthlyContributionEnabled: backendData.corporateMonthlyContributionEnabled || false,
      
      // Expected Returns (with checkboxes)
      rrspExpectedReturn: backendData.rrspExpectedReturn || 0,
      rrspExpectedReturnEnabled: backendData.rrspExpectedReturnEnabled || false,
      tfsaExpectedReturn: backendData.tfsaExpectedReturn || 0,
      tfsaExpectedReturnEnabled: backendData.tfsaExpectedReturnEnabled || false,
      nonRegisteredExpectedReturn: backendData.nonRegisteredExpectedReturn || 0,
      nonRegisteredExpectedReturnEnabled: backendData.nonRegisteredExpectedReturnEnabled || false,
      corporateExpectedReturn: backendData.corporateExpectedReturn || 0,
      corporateExpectedReturnEnabled: backendData.corporateExpectedReturnEnabled || false,
      
      // Spouse Financial Balances
      spouseRrspBalance: backendData.spouseRrspBalance || 0,
      spouseRrifBalance: backendData.spouseRrifBalance || 0,
      spouseLiraBalance: backendData.spouseLiraBalance || 0,
      spouseTfsaBalance: backendData.spouseTfsaBalance || 0,
      spouseTfsaContributionRoom: backendData.spouseTfsaContributionRoom || 88000,
      spouseTfsaAnnualIncrease: backendData.spouseTfsaAnnualIncrease || 7000,
      spouseNonRegisteredBalance: backendData.spouseNonRegisteredBalance || 0,
      spouseCorporateBalance: backendData.spouseCorporateBalance || 0,
      
      // Investment Parameters
      rrspReturnRate: backendData.rrspReturnRate || 6.5,
      rrspRiskLevel: backendData.rrspRiskLevel || 3,
      rrspRrifConversionStrategy: backendData.rrspRrifConversionStrategy || 'Calculate Optimal Conversion Age',
      rrifReturnRate: backendData.rrifReturnRate || 5.5,
      rrifRiskLevel: backendData.rrifRiskLevel || 2,
      liraReturnRate: backendData.liraReturnRate || 6.0,
      liraRiskLevel: backendData.liraRiskLevel || 3,
      liraConversionStrategy: backendData.liraConversionStrategy || 'Calculate Optimal Conversion Age',
      tfsaReturnRate: backendData.tfsaReturnRate || 6.0,
      tfsaRiskLevel: backendData.tfsaRiskLevel || 3,
      nonRegisteredReturnRate: backendData.nonRegisteredReturnRate || 5.0,
      nonRegisteredRiskLevel: backendData.nonRegisteredRiskLevel || 2,
      corporateReturnRate: backendData.corporateReturnRate || 6.0,
      corporateRiskLevel: backendData.corporateRiskLevel || 3,
      
      // Income & Employment
      workingProfile: backendData.workingProfile || 'Steady Income',
      workingAge: backendData.workingAge || 45,
      spouseWorkingProfile: backendData.spouseWorkingProfile || 'Steady Income',
      spouseWorkingAge: backendData.spouseWorkingAge || 45,
      spouseRetirementAge: backendData.spouseRetirementAge || 65,
      
      // Government Benefits
      cppBenefit: backendData.cppBenefit || 15000,
      oasBenefit: backendData.oasBenefit || 8000,
      oasDelayStrategy: backendData.oasDelayStrategy || 'Start at 65',
      cppDelayStrategy: backendData.cppDelayStrategy || 'Start at 65',
      oasDelay: backendData.oasDelay || 0,
      cppDelay: backendData.cppDelay || 0,
      spouseCppBenefit: backendData.spouseCppBenefit || 12000,
      spouseOasBenefit: backendData.spouseOasBenefit || 8000,
      spouseOasDelayStrategy: backendData.spouseOasDelayStrategy || 'Start at 65',
      spouseCppDelayStrategy: backendData.spouseCppDelayStrategy || 'Start at 65',
      
      // Government Benefits Optimization
      cppStartAge: backendData.cppStartAge || 65,
      oasStartAge: backendData.oasStartAge || 65,
      optimizeCpp: backendData.optimizeCpp || false,
      optimizeOas: backendData.optimizeOas || false,
      
      // Pension Information
      pensionAmount: backendData.pensionAmount || 0,
      pensionStartAge: backendData.pensionStartAge || 65,
      pensionInflationRate: backendData.pensionInflationRate || 2.0,
      pensionSurvivorBenefitEnabled: backendData.pensionSurvivorBenefitEnabled || 0,
      pensionSurvivorBenefitPercent: backendData.pensionSurvivorBenefitPercent || 0,
      
      // Spouse Pension
      spousePensionAmount: backendData.spousePensionAmount,
      spousePensionStartAge: backendData.spousePensionStartAge,
      spousePensionInflationRate: backendData.spousePensionInflationRate,
      spousePensionSurvivorBenefitEnabled: backendData.spousePensionSurvivorBenefitEnabled,
      
      // Other Income
      otherIncomeAmount: backendData.otherIncomeAmount || 0,
      otherIncomeStartAge: backendData.otherIncomeStartAge || 65,
      otherIncomeProfile: backendData.otherIncomeProfile || 'Steady',
      
      // Spouse Other Income
      spouseOtherIncomeAmount: backendData.spouseOtherIncomeAmount,
      spouseOtherIncomeStartAge: backendData.spouseOtherIncomeStartAge,
      spouseOtherIncomeProfile: backendData.spouseOtherIncomeProfile,
      
      // Spending & Lifestyle - NEW TABLE STRUCTURE
      sharedSpendingStages: backendData.sharedSpendingStages || [
        {
          stage: 1,
          startingAge: backendData.age || 45,
          endingAge: (backendData.retirementAge || 65) - 1,
          amount: backendData.annualSpending || 60000
        },
        {
          stage: 2,
          startingAge: backendData.retirementAge || 65,
          endingAge: (backendData.retirementAge || 65) + 9,
          amount: backendData.spendingPhaseA || 60000
        },
        {
          stage: 3,
          startingAge: (backendData.retirementAge || 65) + 10,
          endingAge: (backendData.retirementAge || 65) + 19,
          amount: backendData.spendingPhaseB || 50000
        },
        {
          stage: 4,
          startingAge: (backendData.retirementAge || 65) + 20,
          endingAge: (backendData.retirementAge || 65) + 20,
          amount: backendData.spendingPhaseC || 40000
        }
      ],
      personalSpendingEnabled: backendData.personalSpendingEnabled || false,
      personalSpendingStages: backendData.personalSpendingStages,
      
      // Legacy spending fields (keep for backward compatibility)
      annualSpending: backendData.annualSpending || 60000,
      spendingPhaseA: backendData.spendingPhaseA || 60000,
      spendingPhaseB: backendData.spendingPhaseB || 50000,
      spendingPhaseC: backendData.spendingPhaseC || 40000,
      
      // Capital Gains Planning
      capitalGainsA: backendData.capitalGainsA || 0,
      capitalGainsYearA: backendData.capitalGainsYearA || 65,
      capitalGainsB: backendData.capitalGainsB || 0,
      capitalGainsYearB: backendData.capitalGainsYearB || 70,
      capitalGainsC: backendData.capitalGainsC || 0,
      capitalGainsYearC: backendData.capitalGainsYearC || 75,
      capitalGainsD: backendData.capitalGainsD || 0,
      capitalGainsYearD: backendData.capitalGainsYearD || 80,
      
      // Advanced Settings
      inflationRate: backendData.inflationRate || 2.5,
      taxProvince: backendData.taxProvince || 'Ontario',
      taxOptimization: backendData.taxOptimization || true,
      
      // Withdrawal Optimization
      withdrawalStrategy: backendData.withdrawalStrategy,
      taxImpactAnalysis: backendData.taxImpactAnalysis,
      estatePreservationTarget: backendData.estatePreservationTarget,
      showRiskPercentiles: backendData.showRiskPercentiles,
      
      // Income Splitting
      enableIncomeSplitting: backendData.enableIncomeSplitting || false,
      pensionIncomeSplittingPercent: backendData.pensionIncomeSplittingPercent || 50,
      splitRRIFIncome: backendData.splitRRIFIncome || false,
      splitRPPIncome: backendData.splitRPPIncome || false,
      
      // Tax Bracket Optimization
      targetTaxBracket: backendData.targetTaxBracket,
      avoidClawback: backendData.avoidClawback,
      maxOutTaxBracket: backendData.maxOutTaxBracket,
      useTFSAForTaxBracket: backendData.useTFSAForTaxBracket,
      
      // Withholding Tax
      includeWithholdingTax: backendData.includeWithholdingTax
    };
  }

  // Convert frontend UserProfile to backend format
  static convertToBackendFormat(frontendProfile: UserProfile): any {
    return {
      // Personal Information
      age: frontendProfile.age,
      gender: frontendProfile.gender,
      life_expectancy_scenario: toLifeExpectancyCode(frontendProfile.lifeExpectancyScenario),
      spouse_age: frontendProfile.spouseAge || null,
      spouse_gender: frontendProfile.spouseGender || null,
      // The Spouse Information select shows 'General' until touched; post what it shows so the engine never gets null
      spouse_life_expectancy_scenario: toLifeExpectancyCode(
        frontendProfile.spouseLifeExpectancyScenario || (frontendProfile.spouseAge ? 'General' : undefined)
      ),
      
      // General Settings
      current_year: frontendProfile.currentYear,
      retirement_age: frontendProfile.retirementAge,
      life_expectancy: frontendProfile.lifeExpectancy,
      num_scenarios: frontendProfile.numScenarios,
      province: frontendProfile.province,
      marital_status: frontendProfile.maritalStatus,
      
      // Income Information - NEW TABLE STRUCTURE
      income_stages: toBackendIncomeStages(frontendProfile.incomeStages),
      spouse_income_stages: toBackendIncomeStages(frontendProfile.spouseIncomeStages),
      
      // Legacy income fields (keep for backward compatibility)
      current_income: frontendProfile.currentIncome,
      expected_retirement_income: frontendProfile.expectedRetirementIncome,
      spouse_current_income: frontendProfile.spouseCurrentIncome || null,
      spouse_expected_retirement_income: frontendProfile.spouseExpectedRetirementIncome || null,
      
      // Financial Balances
      rrsp_balance: frontendProfile.rrspBalance,
      rrif_balance: frontendProfile.rrifBalance,
      lira_balance: frontendProfile.liraBalance,
      tfsa_balance: frontendProfile.tfsaBalance,
      tfsa_contribution_room: frontendProfile.tfsaContributionRoom,
      tfsa_annual_increase: frontendProfile.tfsaAnnualIncrease,
      non_registered_balance: frontendProfile.nonRegisteredBalance,
      corporate_balance: frontendProfile.corporateBalance,
      
      // Monthly Contributions (with checkboxes)
      rrsp_monthly_contribution: frontendProfile.rrspMonthlyContribution,
      rrsp_monthly_contribution_enabled: frontendProfile.rrspMonthlyContributionEnabled,
      tfsa_monthly_contribution: frontendProfile.tfsaMonthlyContribution,
      tfsa_monthly_contribution_enabled: frontendProfile.tfsaMonthlyContributionEnabled,
      non_registered_monthly_contribution: frontendProfile.nonRegisteredMonthlyContribution,
      non_registered_monthly_contribution_enabled: frontendProfile.nonRegisteredMonthlyContributionEnabled,
      corporate_monthly_contribution: frontendProfile.corporateMonthlyContribution,
      corporate_monthly_contribution_enabled: frontendProfile.corporateMonthlyContributionEnabled,
      
      // Expected Returns (with checkboxes)
      rrsp_expected_return: frontendProfile.rrspExpectedReturn,
      rrsp_expected_return_enabled: frontendProfile.rrspExpectedReturnEnabled,
      tfsa_expected_return: frontendProfile.tfsaExpectedReturn,
      tfsa_expected_return_enabled: frontendProfile.tfsaExpectedReturnEnabled,
      non_registered_expected_return: frontendProfile.nonRegisteredExpectedReturn,
      non_registered_expected_return_enabled: frontendProfile.nonRegisteredExpectedReturnEnabled,
      corporate_expected_return: frontendProfile.corporateExpectedReturn,
      corporate_expected_return_enabled: frontendProfile.corporateExpectedReturnEnabled,
      
      // Spouse Financial Balances
      spouse_rrsp_balance: frontendProfile.spouseRrspBalance || null,
      spouse_rrif_balance: frontendProfile.spouseRrifBalance || null,
      spouse_lira_balance: frontendProfile.spouseLiraBalance || null,
      spouse_tfsa_balance: frontendProfile.spouseTfsaBalance || null,
      spouse_tfsa_contribution_room: frontendProfile.spouseTfsaContributionRoom || null,
      spouse_tfsa_annual_increase: frontendProfile.spouseTfsaAnnualIncrease || null,
      spouse_non_registered_balance: frontendProfile.spouseNonRegisteredBalance || null,
      spouse_corporate_balance: frontendProfile.spouseCorporateBalance || null,
      
      // Investment Parameters
      rrsp_return_rate: frontendProfile.rrspReturnRate,
      rrsp_risk_level: frontendProfile.rrspRiskLevel,
      rrsp_rrif_conversion_strategy: frontendProfile.rrspRrifConversionStrategy,
      rrif_return_rate: frontendProfile.rrifReturnRate,
      rrif_risk_level: frontendProfile.rrifRiskLevel,
      lira_return_rate: frontendProfile.liraReturnRate,
      lira_risk_level: frontendProfile.liraRiskLevel,
      lira_conversion_strategy: frontendProfile.liraConversionStrategy,
      tfsa_return_rate: frontendProfile.tfsaReturnRate,
      tfsa_risk_level: frontendProfile.tfsaRiskLevel,
      non_registered_return_rate: frontendProfile.nonRegisteredReturnRate,
      non_registered_risk_level: frontendProfile.nonRegisteredRiskLevel,
      corporate_return_rate: frontendProfile.corporateReturnRate,
      corporate_risk_level: frontendProfile.corporateRiskLevel,

      // Spouse Investment Parameters
      spouse_rrsp_return_rate: frontendProfile.spouseRrspReturnRate,
      spouse_rrsp_risk_level: frontendProfile.spouseRrspRiskLevel,
      spouse_rrsp_rrif_conversion_strategy: frontendProfile.spouseRrspRrifConversionStrategy,
      spouse_rrif_return_rate: frontendProfile.spouseRrifReturnRate,
      spouse_rrif_risk_level: frontendProfile.spouseRrifRiskLevel,
      spouse_lira_return_rate: frontendProfile.spouseLiraReturnRate,
      spouse_lira_risk_level: frontendProfile.spouseLiraRiskLevel,
      spouse_lira_conversion_strategy: frontendProfile.spouseLiraConversionStrategy,
      spouse_tfsa_return_rate: frontendProfile.spouseTfsaReturnRate,
      spouse_tfsa_risk_level: frontendProfile.spouseTfsaRiskLevel,
      spouse_non_registered_return_rate: frontendProfile.spouseNonRegisteredReturnRate,
      spouse_non_registered_risk_level: frontendProfile.spouseNonRegisteredRiskLevel,
      spouse_corporate_return_rate: frontendProfile.spouseCorporateReturnRate,
      spouse_corporate_risk_level: frontendProfile.spouseCorporateRiskLevel,
      
      // Income & Employment
      working_profile: frontendProfile.workingProfile,
      working_age: frontendProfile.workingAge,
      spouse_working_profile: frontendProfile.spouseWorkingProfile,
      spouse_working_age: frontendProfile.spouseWorkingAge,
      spouse_retirement_age: frontendProfile.spouseRetirementAge,
      
      // Government Benefits
      cpp_benefit: frontendProfile.cppBenefit,
      oas_benefit: frontendProfile.oasBenefit,
      oas_delay_strategy: frontendProfile.oasDelayStrategy,
      cpp_delay_strategy: frontendProfile.cppDelayStrategy,

      // Spouse Government Benefits (backend fields: spouse_cpp_amount, spouse_cpp_start_age, spouse_oas_amount, spouse_oas_start_age)
      spouse_cpp_amount: frontendProfile.spouseAge ? frontendProfile.spouseCppBenefit || 0 : null,
      spouse_cpp_start_age: frontendProfile.spouseAge ? toBenefitStartAge(frontendProfile.spouseCppDelayStrategy, 60) : null,
      spouse_oas_amount: frontendProfile.spouseAge ? frontendProfile.spouseOasBenefit || 0 : null,
      spouse_oas_start_age: frontendProfile.spouseAge ? toBenefitStartAge(frontendProfile.spouseOasDelayStrategy, 65) : null,
      
      // Government Benefits Optimization
      cpp_start_age: frontendProfile.cppStartAge,
      oas_start_age: frontendProfile.oasStartAge,
      optimize_cpp: frontendProfile.optimizeCpp,
      optimize_oas: frontendProfile.optimizeOas,
      
      // Pension Information
      pension_amount: frontendProfile.pensionAmount,
      pension_start_age: frontendProfile.pensionStartAge,
      pension_inflation_rate: frontendProfile.pensionInflationRate,
      pension_survivor_benefit: frontendProfile.pensionSurvivorBenefitEnabled
        ? (frontendProfile.pensionSurvivorBenefitPercent || 0) * 100
        : 0,

      // Spouse Pension
      spouse_pension_amount: frontendProfile.spousePensionAmount || null,
      spouse_pension_start_age: frontendProfile.spousePensionStartAge || null,
      spouse_pension_inflation_rate: frontendProfile.spousePensionInflationRate || null,
      spouse_pension_survivor_benefit: frontendProfile.spousePensionSurvivorBenefitEnabled
        ? (frontendProfile.spousePensionSurvivorBenefitPercent || 0) * 100
        : 0,
      
      // Other Income cards, timed by each person's own age (home/cottage sales, inheritances, rental income)
      other_income_sources: toBackendOtherIncomeSources(frontendProfile.otherIncomeSources),
      spouse_other_income_sources: frontendProfile.spouseAge
        ? toBackendOtherIncomeSources(frontendProfile.spouseOtherIncomeSources)
        : null,
      
      // Spending & Lifestyle - NEW TABLE STRUCTURE
      shared_spending_stages: frontendProfile.sharedSpendingStages,
      personal_spending_enabled: frontendProfile.personalSpendingEnabled,
      personal_spending_stages: frontendProfile.personalSpendingStages || null,
      
      // Legacy spending fields (keep for backward compatibility)
      annual_spending: spendingAtRetirement(frontendProfile),
      spending_scenario: frontendProfile.spendingScenario || 'exact',
      spending_phase_a: frontendProfile.spendingPhaseA,
      spending_phase_b: frontendProfile.spendingPhaseB,
      spending_phase_c: frontendProfile.spendingPhaseC,
      
      // Capital Gains Planning
      capital_gains_a: frontendProfile.capitalGainsA,
      capital_gains_year_a: toCapitalGainsYear(frontendProfile, frontendProfile.capitalGainsA, frontendProfile.capitalGainsYearA),
      capital_gains_b: frontendProfile.capitalGainsB,
      capital_gains_year_b: toCapitalGainsYear(frontendProfile, frontendProfile.capitalGainsB, frontendProfile.capitalGainsYearB),
      capital_gains_c: frontendProfile.capitalGainsC,
      capital_gains_year_c: toCapitalGainsYear(frontendProfile, frontendProfile.capitalGainsC, frontendProfile.capitalGainsYearC),
      capital_gains_d: frontendProfile.capitalGainsD,
      capital_gains_year_d: toCapitalGainsYear(frontendProfile, frontendProfile.capitalGainsD, frontendProfile.capitalGainsYearD),
      
      // Advanced Settings
      inflation_rate: frontendProfile.inflationRate,
      tax_province: frontendProfile.taxProvince,
      tax_optimization: frontendProfile.taxOptimization,
      
      // Withdrawal Optimization
      withdrawal_strategy: frontendProfile.withdrawalStrategy,
      tax_impact_analysis: frontendProfile.taxImpactAnalysis,
      estate_preservation_target: frontendProfile.estatePreservationTarget,
      show_risk_percentiles: frontendProfile.showRiskPercentiles,
      
      // Income Splitting
      enable_income_splitting: frontendProfile.enableIncomeSplitting,
      pension_income_splitting_percent: frontendProfile.pensionIncomeSplittingPercent,
      split_rrif_income: frontendProfile.splitRRIFIncome,
      split_rpp_income: frontendProfile.splitRPPIncome,
      
      // Tax Bracket Optimization
      target_tax_bracket: frontendProfile.targetTaxBracket,
      avoid_clawback: frontendProfile.avoidClawback,
      max_out_tax_bracket: frontendProfile.maxOutTaxBracket,
      use_tfsa_for_tax_bracket: frontendProfile.useTFSAForTaxBracket,
      
      // Withholding Tax
      include_withholding_tax: frontendProfile.includeWithholdingTax
    };
  }

  static async runAnalysis(userProfile: UserProfile): Promise<MonteCarloResponse> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${API_BASE_URL}/api/v1/advanced/monte-carlo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(AdvancedRetirementAPI.convertToBackendFormat(userProfile)),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        await throwServerError(response);
      }

      const data = await response.json();
      return data as MonteCarloResponse;
    } catch (error) {
      console.error('Error running analysis:', error);

      // Return mock data for development/testing
      return AdvancedRetirementAPI.getMockAnalysisResponse(userProfile);
    }
  }

  static async optimizeTaxStrategy(
    userProfile: UserProfile,
    numMonteCarloRuns: number = 10,
    lockedOasAge: number | null = null,
    lockedCppAge: number | null = null
  ): Promise<any> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

      const requestBody = {
        ...AdvancedRetirementAPI.convertToBackendFormat(userProfile),
        num_monte_carlo_runs: numMonteCarloRuns,
        round3_income_step: 1000,  // $1,000 granularity
        round3_income_range: 10000,  // ±$10,000 range
        locked_oas_age: lockedOasAge,  // null = optimize, number = lock to that age
        locked_cpp_age: lockedCppAge   // null = optimize, number = lock to that age
      };

      const response = await fetch(`${API_BASE_URL}/api/v1/advanced/optimize-tax-strategy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        await throwServerError(response);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error optimizing tax strategy:', error);
      throw error;
    }
  }

  static async compareRetirementStrategies(
    userProfile: UserProfile,
    numScenarios: number = 100
  ): Promise<any> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000); // 3 minute timeout (runs 2 optimizations)

      const requestBody = {
        ...AdvancedRetirementAPI.convertToBackendFormat(userProfile),
        num_scenarios: numScenarios
      };

      const response = await fetch(`${API_BASE_URL}/api/v1/advanced/compare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        await throwServerError(response);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error comparing retirement strategies:', error);
      throw error;
    }
  }

  static getMockAnalysisResponse(userProfile: UserProfile): MonteCarloResponse {
    // Generate realistic mock data for testing
    const baseBalance = userProfile.rrspBalance + userProfile.tfsaBalance + userProfile.nonRegisteredBalance + (userProfile.corporateBalance || 0);
    const mockBalance = baseBalance * (1 + (userProfile.retirementAge - userProfile.age) * 0.06);
    
    return {
      summary: {
        total_scenarios: 1000,
        percentiles: {
          p25_balance: mockBalance * 0.8,
          p50_balance: mockBalance,
          p75_balance: mockBalance * 1.2
        },
        recommendations: [
          "Consider increasing RRSP contributions to maximize tax efficiency",
          "Monitor investment performance and adjust risk tolerance as needed",
          "Review withdrawal strategy annually based on market conditions"
        ]
      },
      scenarios: {
        low: {
          scenario_type: "Conservative",
          total_final_balance: mockBalance * 0.8,
          life_expectancy: 85,
          success_rate: 0.75,
          rrsp_contribution: 18000,
          detailed_projection_data: [],
          chart_data: {}
        },
        average: {
          scenario_type: "Moderate",
          total_final_balance: mockBalance,
          life_expectancy: 87,
          success_rate: 0.85,
          rrsp_contribution: 20000,
          detailed_projection_data: [],
          chart_data: {}
        },
        high: {
          scenario_type: "Aggressive",
          total_final_balance: mockBalance * 1.2,
          life_expectancy: 89,
          success_rate: 0.95,
          rrsp_contribution: 22000,
          detailed_projection_data: [],
          chart_data: {}
        }
      }
    };
  }

  static calculateAfterTaxEndingBalance(simulationData: any): number {
    // Enhanced mathematical calculation with realistic tax rates
    let baseBalance = 0;
    
    if (simulationData.summary?.percentiles?.p50_balance) {
      baseBalance = simulationData.summary.percentiles.p50_balance;
    } else if (simulationData.summary_stats?.median_final_balance) {
      baseBalance = simulationData.summary_stats.median_final_balance;
    }
    
    if (baseBalance === 0) return 0;
    
    // Mathematical tax calculation based on account composition
    // This is where the withdrawal strategy affects the final balance
    const accountComposition = this.estimateAccountComposition(baseBalance);
    
    // Calculate taxes based on withdrawal order and account types
    const totalTaxes = this.calculateAdvancedTaxes(accountComposition, simulationData.user_profile);
    
    return baseBalance - totalTaxes;
  }

  static estimateAccountComposition(totalBalance: number): any {
    // Estimate typical account composition (this would come from the simulation)
    return {
      tfsa: totalBalance * 0.15,        // 15% TFSA
      nonRegistered: totalBalance * 0.25, // 25% Non-registered
      rrspRrif: totalBalance * 0.45,     // 45% RRSP/RRIF
      corporate: totalBalance * 0.15      // 15% Corporate
    };
  }

  static calculateTotalTaxes(accountComposition: any): number {
    let totalTaxes = 0;
    
    // TFSA: No taxes
    // totalTaxes += accountComposition.tfsa * 0; // 0% tax
    
    // Non-registered: Capital gains (50% inclusion, 25% tax rate)
    totalTaxes += accountComposition.nonRegistered * 0.5 * 0.25; // 12.5% effective
    
    // RRSP/RRIF: Fully taxable (25% tax rate)
    totalTaxes += accountComposition.rrspRrif * 0.25; // 25% tax
    
    // Corporate: Corporate tax + personal tax
    totalTaxes += accountComposition.corporate * 0.15; // 15% corporate + 10% personal = 25% total
    
    return totalTaxes;
  }

  static calculateAdvancedTaxes(accountComposition: any, userProfile: any): number {
    let totalTaxes = 0;
    
    // Base tax calculations
    const baseTaxes = this.calculateTotalTaxes(accountComposition);
    totalTaxes += baseTaxes;
    
    // Income splitting optimization
    if (userProfile.enableIncomeSplitting && userProfile.spouseIncomeLevel === 'low') {
      // Reduce taxes by splitting pension income to lower-income spouse
      const splitAmount = accountComposition.rrspRrif * (userProfile.pensionIncomeSplittingPercent || 50) / 100;
      const taxSavings = splitAmount * 0.15; // Assume 15% tax savings
      totalTaxes -= taxSavings;
    }
    
    // Withholding tax calculations
    if (userProfile.includeWithholdingTax) {
      const rrifWithdrawals = accountComposition.rrspRrif;
      let withholdingTax = 0;
      
      if (userProfile.province === 'Quebec') {
        // Quebec rates: 5%, 10%, 15% federal + 16% provincial
        if (rrifWithdrawals <= 5000) {
          withholdingTax = rrifWithdrawals * 0.21; // 5% + 16%
        } else if (rrifWithdrawals <= 15000) {
          withholdingTax = rrifWithdrawals * 0.26; // 10% + 16%
        } else {
          withholdingTax = rrifWithdrawals * 0.31; // 15% + 16%
        }
      } else {
        // Other provinces: 10%, 20%, 30%
        if (rrifWithdrawals <= 5000) {
          withholdingTax = rrifWithdrawals * 0.10;
        } else if (rrifWithdrawals <= 15000) {
          withholdingTax = rrifWithdrawals * 0.20;
        } else {
          withholdingTax = rrifWithdrawals * 0.30;
        }
      }
      
      totalTaxes += withholdingTax;
    }
    
    // Tax bracket optimization
    if (userProfile.maxOutTaxBracket) {
      // Assume some tax savings from staying in current bracket
      const bracketSavings = accountComposition.rrspRrif * 0.05; // 5% savings
      totalTaxes -= bracketSavings;
    }
    
    // Government benefit clawback avoidance
    if (userProfile.avoidClawback) {
      // Assume some tax savings from avoiding clawback
      const clawbackSavings = accountComposition.rrspRrif * 0.03; // 3% savings
      totalTaxes -= clawbackSavings;
    }
    
    return Math.max(0, totalTaxes); // Ensure taxes don't go negative
  }

  static async runWithdrawalStrategyOptimization(userProfile: UserProfile, numScenarios: number = 100): Promise<MonteCarloResponse> {
    try {
      const strategies = ['minimize-tax', 'preserve-estate', 'manage-risk', 'grow-net-worth'];
      const results: any[] = [];

      console.log('🔬 Starting mathematical optimization...');

      // Run simulation for each withdrawal strategy
      for (const strategy of strategies) {
        console.log(`📊 Running scenario for strategy: ${strategy}`);
        
        const profileWithStrategy = { ...userProfile, withdrawalStrategy: strategy as 'minimize-tax' | 'preserve-estate' | 'manage-risk' | 'grow-net-worth' };
        const backendProfile = this.convertToBackendFormat(profileWithStrategy);
        
        const request = {
          user_profile: backendProfile,
          num_scenarios: numScenarios,
          rrsp_contribution_range: [0, 30000]
        };

        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
          
          const response = await fetch(`${API_BASE_URL}/monte-carlo`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          const afterTaxBalance = this.calculateAfterTaxEndingBalance(data);
          
          console.log(`💰 ${strategy}: After-tax balance = $${afterTaxBalance.toLocaleString()}`);
          
          results.push({
            strategy,
            data,
            afterTaxBalance
          });
        } catch (error) {
          // If backend is not available, use mock data
          if (error instanceof TypeError && (
            error.message.includes('Failed to fetch') || 
            error.message.includes('NetworkError') ||
            error.message.includes('ERR_CONNECTION_REFUSED') ||
            error.message.includes('ERR_CONNECTION_TIMED_OUT')
          ) || (error instanceof Error && error.name === 'AbortError')) {
            console.log(`Backend not available, using mock data for ${strategy}...`);
            const mockData = this.getMockAnalysisResponse(profileWithStrategy);
            const afterTaxBalance = this.calculateAfterTaxEndingBalance(mockData);
            
            console.log(`💰 ${strategy}: After-tax balance = $${afterTaxBalance.toLocaleString()}`);
            
            results.push({
              strategy,
              data: mockData,
              afterTaxBalance
            });
          } else {
            throw error;
          }
        }
      }

      // Find the optimal strategy (highest after-tax ending balance)
      const optimalResult = this.findOptimalStrategy(results);
      
      console.log(`🏆 Mathematical optimization complete. Optimal strategy: ${optimalResult.optimizationAnalysis?.optimalStrategy}`);
      
      return optimalResult;
    } catch (error) {
      console.error('Withdrawal strategy optimization error:', error);
      throw error;
    }
  }

  static findOptimalStrategy(results: any[]): MonteCarloResponse {
    let bestStrategy = null;
    let bestAfterTaxBalance = -Infinity;

    for (const result of results) {
      const afterTaxBalance = this.calculateAfterTaxEndingBalance(result.data);
      
      if (afterTaxBalance > bestAfterTaxBalance) {
        bestAfterTaxBalance = afterTaxBalance;
        bestStrategy = result;
      }
    }

    // Add optimization analysis to the best result
    if (bestStrategy) {
      bestStrategy.data.optimizationAnalysis = {
        strategies: results.map(r => ({
          strategy: r.strategy,
          afterTaxBalance: this.calculateAfterTaxEndingBalance(r.data)
        })),
        optimalStrategy: bestStrategy.strategy,
        optimalAfterTaxBalance: bestAfterTaxBalance
      };
    }

    return bestStrategy?.data || results[0]?.data;
  }

  static async getLifeExpectancy(age: number, gender: string, scenario: string): Promise<number> {
    try {
      const response = await fetch(`${API_BASE_URL}/life-expectancy?age=${age}&gender=${gender}&scenario=${scenario}`);
      
      if (!response.ok) {
        await throwServerError(response);
      }

      const data = await response.json();
      return data.life_expectancy;
    } catch (error) {
      console.error('Error getting life expectancy:', error);
      // Return default life expectancy based on age
      return Math.max(85, age + 20);
    }
  }

  static async calculateTaxes(age: number, income: number, province: string): Promise<number> {
    try {
      const response = await fetch(`${API_BASE_URL}/taxes?age=${age}&income=${income}&province=${province}`);
      
      if (!response.ok) {
        await throwServerError(response);
      }

      const data = await response.json();
      return data.total_taxes;
    } catch (error) {
      console.error('Error calculating taxes:', error);
      // Return simplified tax calculation
      return income * 0.25; // 25% effective tax rate
    }
  }

  static async getRRIFRate(age: number): Promise<number> {
    try {
      const response = await fetch(`${API_BASE_URL}/rrif-rate?age=${age}`);
      
      if (!response.ok) {
        await throwServerError(response);
      }

      const data = await response.json();
      return data.withdrawal_rate;
    } catch (error) {
      console.error('Error getting RRIF rate:', error);
      // Return default RRIF rates
      if (age < 71) return 0;
      if (age < 81) return 0.05;
      if (age < 91) return 0.06;
      return 0.20;
    }
  }

  static async getGovernmentBenefits(age: number, income: number, province: string): Promise<{ cpp: number; oas: number }> {
    try {
      const response = await fetch(`${API_BASE_URL}/government-benefits?age=${age}&income=${income}&province=${province}`);
      
      if (!response.ok) {
        await throwServerError(response);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting government benefits:', error);
      // Return default benefits
      return {
        cpp: age >= 65 ? 8000 : 0,
        oas: age >= 65 ? 7000 : 0
      };
    }
  }

  static async optimizeGovernmentBenefits(userProfile: UserProfile): Promise<any> {
    const backendUserProfile = this.convertToBackendFormat(userProfile);
    
    const request = {
      user_profile: backendUserProfile,
      num_scenarios: 1000,
      rrsp_contribution_range: [0, 30000]
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/advanced/optimize-benefits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        await throwServerError(response);
      }

      return await response.json();
    } catch (error) {
      console.error('Error optimizing government benefits:', error);
      throw error;
    }
  }

  // Helper method to create a default user profile with all required fields
  static createDefaultUserProfile(): UserProfile {
    return {
      // Personal Information
      age: 45,
      gender: 'Male',
      lifeExpectancyScenario: 'General',
      spouseAge: undefined, // No spouse by default
      spouseGender: undefined,
      spouseLifeExpectancyScenario: undefined,
      
      // General Settings
      currentYear: 2025,
      retirementAge: 65,
      lifeExpectancy: 85,
      numScenarios: 100,
      province: 'Ontario',
      maritalStatus: 'Single', // Changed from 'Married' to 'Single' as default
      
      // Income Information - NEW TABLE STRUCTURE
      incomeStages: [
        {
          stage: 1,
          startingAge: 45,
          endingAge: 64,
          startingIncome: 80000,
          endingIncome: 80000,
          rrspContributions: 0
        }
      ],
      spouseIncomeStages: undefined,
      
      // Legacy income fields (keep for backward compatibility)
      currentIncome: 80000,
      expectedRetirementIncome: 60000,
      spouseCurrentIncome: undefined,
      spouseExpectedRetirementIncome: undefined,
      
      // Financial Balances
      rrspBalance: 250000,
      rrifBalance: 0,
      liraBalance: 0,
      rrspContributionRoom: 30000,
      tfsaBalance: 50000,
      tfsaContributionRoom: 88000,
      tfsaAnnualIncrease: 7000,
      nonRegisteredBalance: 100000,
      corporateBalance: 0,
      
      // Monthly Contributions (with checkboxes)
      rrspMonthlyContribution: 0,
      rrspMonthlyContributionEnabled: false,
      tfsaMonthlyContribution: 0,
      tfsaMonthlyContributionEnabled: false,
      nonRegisteredMonthlyContribution: 0,
      nonRegisteredMonthlyContributionEnabled: false,
      corporateMonthlyContribution: 0,
      corporateMonthlyContributionEnabled: false,
      
      // Expected Returns (with checkboxes)
      rrspExpectedReturn: 0,
      rrspExpectedReturnEnabled: false,
      tfsaExpectedReturn: 0,
      tfsaExpectedReturnEnabled: false,
      nonRegisteredExpectedReturn: 0,
      nonRegisteredExpectedReturnEnabled: false,
      corporateExpectedReturn: 0,
      corporateExpectedReturnEnabled: false,
      
      // Spouse Financial Balances
      spouseRrspBalance: 0,
      spouseRrifBalance: 0,
      spouseLiraBalance: 0,
      spouseRrspContributionRoom: 25000,
      spouseTfsaBalance: 0,
      spouseTfsaContributionRoom: 88000,
      spouseTfsaAnnualIncrease: 7000,
      spouseNonRegisteredBalance: 0,
      spouseCorporateBalance: 0,
      
      // Investment Parameters
      rrspReturnRate: 6.5,
      rrspRiskLevel: 3,
      rrspRrifConversionStrategy: 'Calculate Optimal Conversion Age', // Default: optimize for pension credit & splitting
      rrifReturnRate: 5.5,
      rrifRiskLevel: 2,
      liraReturnRate: 6.0,
      liraRiskLevel: 3,
      liraConversionStrategy: 'Calculate Optimal Conversion Age', // Default: optimize conversion timing
      tfsaReturnRate: 6.0,
      tfsaRiskLevel: 3,
      nonRegisteredReturnRate: 5.0,
      nonRegisteredRiskLevel: 2,
      corporateReturnRate: 6.0,
      corporateRiskLevel: 3,
      
      // Spouse Investment Parameters
      spouseRrspReturnRate: 6.5,
      spouseRrspRiskLevel: 3,
      spouseRrspRrifConversionStrategy: 'Calculate Optimal Conversion Age',
      spouseRrifReturnRate: 5.5,
      spouseRrifRiskLevel: 2,
      spouseLiraReturnRate: 6.0,
      spouseLiraRiskLevel: 3,
      spouseLiraConversionStrategy: 'Calculate Optimal Conversion Age',
      spouseTfsaReturnRate: 6.0,
      spouseTfsaRiskLevel: 3,
      spouseNonRegisteredReturnRate: 5.0,
      spouseNonRegisteredRiskLevel: 2,
      spouseCorporateReturnRate: 6.0,
      spouseCorporateRiskLevel: 3,
      
      // Income & Employment
      workingProfile: 'Steady Income',
      workingAge: 65,
      spouseWorkingProfile: 'Steady Income',
      spouseWorkingAge: 65,
      spouseRetirementAge: 65,
      
      // Government Benefits
      cppBenefit: 15000,
      oasBenefit: 8000,
      oasDelayStrategy: 'Calculate Suggested Strategy', // the optimizer picks 65-70 unless the user locks an age
      cppDelayStrategy: 'Calculate Suggested Strategy', // the optimizer picks 60-70 unless the user locks an age
      oasDelay: 0,
      cppDelay: 0,
      spouseCppBenefit: 12000,
      spouseOasBenefit: 8000,
      spouseOasDelayStrategy: 'Start at 65',
      spouseCppDelayStrategy: 'Start at 65',

      // CPP Survivor Benefits
      cppSurvivorBenefit: 0,
      spouseCppSurvivorBenefit: 0,

      // Government Benefits Optimization
      cppStartAge: 65,
      oasStartAge: 65,
      optimizeCpp: false,
      optimizeOas: false,
      
      // Pension Information
      pensionAmount: 0,
      pensionStartAge: 65,
      pensionInflationRate: 2.0,
      pensionSurvivorBenefitEnabled: false,
      pensionSurvivorBenefitPercent: 0,
      
      // Spouse Pension
      spousePensionAmount: 0,
      spousePensionStartAge: 65,
      spousePensionInflationRate: 0.02,
      spousePensionSurvivorBenefitEnabled: false,
      
      // Other Income
      otherIncomeAmount: 0,
      otherIncomeStartAge: 65,
      otherIncomeProfile: 'Steady',
      
      // Spouse Other Income
      spouseOtherIncomeAmount: 0,
      spouseOtherIncomeStartAge: 65,
      spouseOtherIncomeProfile: 'Steady',
      
      // Spending & Lifestyle - NEW TABLE STRUCTURE
      sharedSpendingStages: [
        {
          stage: 1,
          startingAge: 45,
          endingAge: 64,
          amount: 60000
        },
        {
          stage: 2,
          startingAge: 65,
          endingAge: 74,
          amount: 60000
        },
        {
          stage: 3,
          startingAge: 75,
          endingAge: 84,
          amount: 50000
        },
        {
          stage: 4,
          startingAge: 85,
          endingAge: 85,
          amount: 40000
        }
      ],
      personalSpendingEnabled: false,
      personalSpendingStages: undefined,
      
      // Legacy spending fields (keep for backward compatibility)
      annualSpending: 60000,
      spendingScenario: 'exact',
      spendingPhaseA: 60000,
      spendingPhaseB: 50000,
      spendingPhaseC: 40000,
      
      // Capital Gains Planning
      capitalGainsA: 0,
      capitalGainsYearA: 65,
      capitalGainsB: 0,
      capitalGainsYearB: 70,
      capitalGainsC: 0,
      capitalGainsYearC: 75,
      capitalGainsD: 0,
      capitalGainsYearD: 80,
      
      // Advanced Settings
      inflationRate: 2.5,
      taxProvince: 'Ontario',
      taxOptimization: true,
      
      // Withdrawal Optimization
      withdrawalStrategy: 'minimize-tax',
      taxImpactAnalysis: true,
      estatePreservationTarget: 100000,
      showRiskPercentiles: true,
      
      // Income Splitting
      enableIncomeSplitting: false,
      pensionIncomeSplittingPercent: 50,
      splitRRIFIncome: false,
      splitRPPIncome: false,
      
      // Tax Bracket Optimization
      targetTaxBracket: 'medium',
      avoidClawback: true,
      maxOutTaxBracket: false,
      useTFSAForTaxBracket: true,
      
      // Withholding Tax
      includeWithholdingTax: true
    };
  }

  // Helper method to create a default married user profile
  static createDefaultMarriedUserProfile(): UserProfile {
    const baseProfile = this.createDefaultUserProfile();
    
    return {
      ...baseProfile,
      spouseAge: 43,
      spouseGender: 'Female',
      spouseLifeExpectancyScenario: 'General',
      spouseCurrentIncome: 75000,
      spouseExpectedRetirementIncome: 55000,
      spousePensionAmount: 0,
      spousePensionStartAge: 65,
      spousePensionInflationRate: 2.0,
      spousePensionSurvivorBenefitEnabled: false,
      spouseOtherIncomeAmount: 0,
      spouseOtherIncomeStartAge: 65,
      spouseOtherIncomeProfile: 'Steady',
      otherIncomeSources: [
        {
          id: '1',
          amount: 0,
          startAge: 65,
          profile: 'Steady',
          incomeType: 'regular',
          description: 'Other Income #1'
        }
      ],
      spouseOtherIncomeSources: [
        {
          id: '1',
          amount: 0,
          startAge: 65,
          profile: 'Steady',
          incomeType: 'regular',
          description: 'Spouse Other Income #1'
        }
      ]
    };
  }

  // Helper method to validate user profile
  static validateUserProfile(profile: UserProfile): string[] {
    const errors: string[] = [];

    // Basic validation
    if (profile.age < 18 || profile.age > 120) {
      errors.push('Age must be between 18 and 120');
    }

    if (profile.currentYear < 2020 || profile.currentYear > 2100) {
      errors.push('Current year must be between 2020 and 2100');
    }

    // Financial validation
    if (profile.rrspBalance < 0) {
      errors.push('RRSP balance cannot be negative');
    }

    if (profile.tfsaBalance < 0) {
      errors.push('TFSA balance cannot be negative');
    }

    if (profile.tfsaContributionRoom < 0) {
      errors.push('TFSA contribution room cannot be negative');
    }

    if (profile.nonRegisteredBalance < 0) {
      errors.push('Non-registered balance cannot be negative');
    }

    // Investment validation
    if (profile.rrspReturnRate < -0.5 || profile.rrspReturnRate > 0.5) {
      errors.push('RRSP return rate must be between -50% and 50%');
    }

    if (profile.rrspRiskLevel < 0 || profile.rrspRiskLevel > 1) {
      errors.push('RRSP risk level must be between 0 and 1');
    }

    // Income validation
    if (profile.currentIncome < 0) {
      errors.push('Current income cannot be negative');
    }

    if (profile.retirementAge < profile.age || profile.retirementAge > 85) {
      errors.push('Retirement age must be between current age and 85');
    }

    // Spending validation
    if (profile.annualSpending < 0) {
      errors.push('Annual spending cannot be negative');
    }

    // Married couple validation
    if (profile.spouseAge) {
      if (profile.spouseAge < 18 || profile.spouseAge > 120) {
        errors.push('Spouse age must be between 18 and 120');
      }
    }

    return errors;
  }

  // Helper method to calculate age from birth year and month
  static calculateAge(birthYear: number, birthMonth: number, currentYear: number): number {
    const currentMonth = new Date().getMonth() + 1; // getMonth() returns 0-11
    let age = currentYear - birthYear;
    
    if (currentMonth < birthMonth) {
      age--;
    }
    
    return age;
  }

  // Helper method to update age when birth year or month changes
  static updateAgeFromBirth(profile: UserProfile): UserProfile {
    // Since we no longer track birthYear/birthMonth, just return the profile as is
    return profile;
  }

  // Helper method to update spouse age when spouse birth year or month changes
  static updateSpouseAgeFromBirth(profile: UserProfile): UserProfile {
    // Since we no longer track spouseBirthYear/spouseBirthMonth, just return the profile as is
    return profile;
  }

  // Helper method to create user profile from saved data
  static createUserProfileFromData(data: any): UserProfile {
    // If data is already a UserProfile, return it
    if (data && typeof data === 'object' && 'age' in data) {
      return data as UserProfile;
    }
    
    // Otherwise, create default profile and merge with data
    const defaultProfile = this.createDefaultUserProfile();
    return { ...defaultProfile, ...data };
  }
} 